// Typed wrapper around the Telegram.WebApp global injected by telegram-web-app.js.

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

export interface TelegramInitDataUnsafe {
  query_id?: string;
  user?: {
    id: number;
    is_bot?: boolean;
    first_name: string;
    last_name?: string;
    username?: string;
    language_code?: string;
  };
  auth_date?: number;
  hash?: string;
  start_param?: string;
}

export interface TelegramHapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  notificationOccurred(type: 'error' | 'success' | 'warning'): void;
  selectionChanged(): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: TelegramInitDataUnsafe;
  themeParams: TelegramThemeParams;
  colorScheme: 'light' | 'dark';
  isExpanded: boolean;
  isVerticalSwipesEnabled?: boolean;
  HapticFeedback?: TelegramHapticFeedback;
  expand(): void;
  close(): void;
  ready(): void;
  openTelegramLink(url: string): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  // Bot API 7.7+: prevents the Mini App from being dismissed via a vertical
  // swipe gesture. Critical for full-screen map UIs that need to capture
  // pan/drag input themselves.
  disableVerticalSwipes?(): void;
  enableVerticalSwipes?(): void;
  // Bot API 7.7+: prompts the user before the Mini App closes.
  enableClosingConfirmation?(): void;
  openInvoice?(url: string, callback?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void): void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export const tg: TelegramWebApp | undefined = window.Telegram?.WebApp;

// Dev fallback: telegram-web-app.js creates window.Telegram in any browser, but
// outside the real Telegram client `initData` is empty. In that case, fall back
// to VITE_DEV_INIT_DATA (generated by `tsx scripts/sign-init-data.ts`) so the
// webapp can authenticate against /v1/auth/session.
const tgInitData = tg?.initData ?? '';
const devInitData =
  import.meta.env.DEV && !tgInitData ? (import.meta.env.VITE_DEV_INIT_DATA ?? '') : '';

export const initData: string = tgInitData || devInitData;

export const themeParams: TelegramThemeParams = tg?.themeParams ?? {};

// Deep-link parameter. Two sources:
//   1. `tg.initDataUnsafe.start_param` — set when the Mini App is launched
//      via `t.me/<bot>/<app>?startapp=…` or a deep link with `startapp`.
//   2. `?start=…` query string — what we get when an inline `web_app` button
//      from a bot message opens the Mini App. Telegram just hands the URL
//      to the WebView, so the param survives as a normal search param.
// We check (1) first, then (2). Also strip the param from the URL after
// reading so Telegram's WebView cache doesn't re-apply a stale deep link.
function readStartParam(): string | undefined {
  const fromTg = tg?.initDataUnsafe.start_param;
  if (fromTg) return fromTg;
  if (typeof window === 'undefined') return undefined;
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get('start') ?? undefined;
  if (fromUrl) {
    url.searchParams.delete('start');
    window.history.replaceState(null, '', url.toString());
  }
  return fromUrl;
}

export const startParam: string | undefined = readStartParam();

export function expand(): void {
  tg?.expand();
}

export function openExternal(url: string): void {
  if (tg && typeof tg.openLink === 'function') {
    tg.openLink(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function openInvoice(url: string, cb?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void): void {
  if (tg && typeof tg.openInvoice === 'function') {
    tg.openInvoice(url, cb);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function openTelegramLink(url: string): void {
  if (tg && typeof tg.openTelegramLink === 'function') {
    tg.openTelegramLink(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  tg?.HapticFeedback?.impactOccurred(style);
}
